'use client';

import React, { useEffect, useRef } from 'react';

import { sanitize } from 'isomorphic-dompurify';

import useWebAlias from '@/helpers/hooks/useWebalias';
import cn from '@/helpers/utils/classNames';

type HtmlRendererProps = { html: string | TrustedHTML } & (
  | ({
      as?: 'div';
    } & Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'dangerouslySetInnerHTML'>)
  | ({
      as: 'p';
    } & Omit<
      React.DetailedHTMLProps<React.HTMLAttributes<HTMLParagraphElement>, HTMLParagraphElement>,
      'dangerouslySetInnerHTML'
    >)
  | ({
      as: 'h2';
    } & Omit<
      React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>,
      'dangerouslySetInnerHTML'
    >)
  | ({
      as: 'span';
    } & Omit<
      React.DetailedHTMLProps<React.HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>,
      'dangerouslySetInnerHTML'
    >)
);

function isString(obj: unknown): obj is String {
  return typeof (obj as String).toUpperCase !== 'undefined';
}

function HtmlRenderer_({ html, className, as = 'div', ...restProps }: HtmlRendererProps) {
  const { webalias } = useWebAlias();
  const rendererRef = useRef<HTMLDivElement | HTMLParagraphElement | HTMLHeadingElement | HTMLSpanElement>(null);

  useEffect(() => {
    let unRegister: Function[] = [];

    const handleClickOnAnchor = (e: MouseEvent) => {
      e.preventDefault();
      const el = e.target as HTMLAnchorElement;
      const href = el.getAttribute('href');
      if (!href) {
        console.log('not navigating as href is undefined', href);
        return;
      }

      const isCtrlCmdPressed = e.ctrlKey || e.metaKey;
      const target = isCtrlCmdPressed ? '_blank' : el.getAttribute('target') || '_self';

      const newHref = href.replace('{0}', webalias).replace('%7B0%7D', webalias);
      window.open(newHref, target);
    };

    const handleContextMenu = (e: MouseEvent) => e.preventDefault();

    if (rendererRef.current) {
      const anchors = rendererRef.current.querySelectorAll('a');
      unRegister = Array.from(anchors).map(anchor => {
        anchor.addEventListener('click', handleClickOnAnchor);
        anchor.addEventListener('contextmenu', handleContextMenu);
        return () => {
          anchor.removeEventListener('click', handleClickOnAnchor);
          anchor.removeEventListener('contextmenu', handleContextMenu);
        };
      });
    }

    return () => unRegister.forEach(fn => fn());
  }, [webalias]);

  const Component = as || 'div';

  return (
    <Component
      ref={rendererRef as any}
      {...restProps}
      className={cn(className)}
      dangerouslySetInnerHTML={{ __html: isString(html) ? sanitize(html) : html }}
    />
  );
}

const HtmlRenderer = React.memo(HtmlRenderer_);

export default HtmlRenderer;
